/**
 * Events Calendar - Google Calendar API Version
 * - Uses Google Calendar API (shows all events!)
 * - LocalStorage caching (5min)
 * - Parallel product fetching
 * - Fallback images for Track/Run/Conditioning Club
 * - DD/MM date format
 * - Displays only next 30 days
 */

class EventsCalendar {
  constructor(container) {
    this.container = container;
    this.calendarId = container.dataset.calendarId;
    this.apiKey = container.dataset.apiKey || 'AIzaSyC0o8Wp_8vPPj5DmKl64ZMKPS921OT63FA';
    
    this.events = [];
    this.filteredEvents = [];
    this.productCache = new Map();
    this.cacheKey = 'twp_events_cache_api_final';
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    
    this.filters = {
      locations: new Set(),
      availability: new Set(),
      dateRange: 'all'
    };
    this.currentPreviewIndex = 0;

    this.elements = {
      filterToggle: container.querySelector('[data-filter-toggle]'),
      filterToggleText: container.querySelector('[data-filter-toggle-text]'),
      filterCount: container.querySelector('[data-filter-count]'),
      filtersPanel: container.querySelector('[data-filters-panel]'),
      locationFilters: container.querySelector('[data-filter-locations]'),
      availabilityInputs: container.querySelectorAll('[data-filter-availability-input]'),
      dateInputs: container.querySelectorAll('[data-filter-date-input]'),
      clearFilters: container.querySelector('[data-clear-filters]'),
      eventsList: container.querySelector('[data-events-list]'),
      loading: container.querySelector('[data-loading]'),
      emptyState: container.querySelector('[data-empty-state]'),
      errorState: container.querySelector('[data-error-state]'),
      previewContainer: container.querySelector('[data-preview-container]')
    };

    this.init();
  }

  async init() {
    this.bindEvents();
    await this.fetchEvents();
  }

  bindEvents() {
    this.elements.filterToggle?.addEventListener('click', () => this.toggleFilters());
    this.elements.availabilityInputs.forEach(input => {
      input.addEventListener('change', () => this.handleAvailabilityFilter(input));
    });
    this.elements.dateInputs.forEach(input => {
      input.addEventListener('change', () => this.handleDateFilter(input));
    });
    this.elements.clearFilters?.addEventListener('click', () => this.clearAllFilters());
  }

  toggleFilters() {
    const isExpanded = this.elements.filterToggle.getAttribute('aria-expanded') === 'true';
    this.elements.filterToggle.setAttribute('aria-expanded', !isExpanded);
    this.elements.filtersPanel.hidden = isExpanded;
    this.elements.filterToggleText.textContent = isExpanded ? 'Show Filters' : 'Hide Filters';
  }

  async fetchEvents() {
    this.showLoading();

    try {
      // Try cache first
      const cached = this.getCache();
      if (cached) {
        this.events = cached;
        this.populateLocationFilters();
        this.applyFilters();
        this.hideLoading();
        
        // Refresh product data in background
        this.refreshProductData();
        return;
      }

      // Fetch from Google Calendar API
      const events = await this.fetchFromCalendarAPI();
      
      this.events = events;
      
      // Save to cache
      this.setCache(this.events);
      
      // Apply filters (including 30-day limit) on initial load
      this.populateLocationFilters();
      this.applyFilters();
      this.hideLoading();

      // Fetch product data in background
      this.refreshProductData();

    } catch (error) {
      console.error('Events fetch error:', error);
      this.showError();
      this.hideLoading();
    }
  }

  async fetchFromCalendarAPI() {
    const now = new Date().toISOString();
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    
    const encodedCalendarId = encodeURIComponent(this.calendarId);
    
    const params = new URLSearchParams({
      key: this.apiKey,
      timeMin: now,
      timeMax: maxDate.toISOString(),
      maxResults: 100,
      singleEvents: true,
      orderBy: 'startTime'
    });

    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodedCalendarId}/events?${params}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('API key invalid or Calendar API not enabled');
      }
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    const events = (data.items || [])
      .map(item => this.normalizeAPIEvent(item))
      .filter(event => event && this.isUpcoming(event.start));
    
    return events.sort((a, b) => new Date(a.start) - new Date(b.start));
  }

  normalizeAPIEvent(apiEvent) {
    if (!apiEvent.start) return null;

    const start = new Date(apiEvent.start.dateTime || apiEvent.start.date);
    const end = new Date(apiEvent.end.dateTime || apiEvent.end.date);
    const allDay = !apiEvent.start.dateTime;

    const description = apiEvent.description || '';
    const productInfo = this.extractProductInfo(description);
    const manualAvailability = this.extractAvailability(description);
    const manualImage = this.extractImage(description);
    
    let location = apiEvent.location || '';
    location = this.cleanLocationName(location);

    const title = apiEvent.summary || 'Event';
    const titleLower = title.toLowerCase();
    
    // Image priority: manual image > event-specific fallback > product image later
    let finalImage = manualImage;
    
    if (!finalImage) {
      // Check for specific event types and use fallback images
      if (titleLower.includes('track club')) {
        finalImage = 'https://cdn.shopify.com/s/files/1/0879/3030/6900/files/C30F3195-BD79-400E-B05B-E50ACC2D41C5.jpg?v=1758736077';
      } else if (titleLower.includes('conditioning club')) {
        finalImage = 'https://cdn.shopify.com/s/files/1/0879/3030/6900/files/BCF2F60D-18EA-405E-98B9-8B82E1D5951A.jpg?v=1758736115';
      } else if (titleLower.includes('run club')) {
        finalImage = 'https://cdn.shopify.com/s/files/1/0879/3030/6900/files/Run_Club_Graphic.jpg?v=1767905496';
      }
    }

    const hasManualAvailability = manualAvailability ? true : false;
    const hasProductHandle = productInfo.handle && productInfo.handle !== 'none';
    
    // Determine availability - Run Club events default to 'free' instead of 'open'
    let finalAvailability = manualAvailability || 'open';
    if (!manualAvailability && titleLower.includes('run club')) {
      finalAvailability = 'free';
    }
    
    return {
      id: apiEvent.id || Math.random().toString(36),
      title: title,
      start: start.toISOString(),
      end: end.toISOString(),
      all_day: allDay,
      date_display: this.formatDateDisplay(start, end, allDay),
      location: location,
      location_badge_class: this.getLocationBadgeClass(location),
      product_url: productInfo.url,
      product_handle: productInfo.handle,
      product_image: finalImage,
      availability: finalAvailability,
      availability_label: this.getAvailabilityLabel(finalAvailability),
      needsProduct: !hasManualAvailability && hasProductHandle
    };
  }

  // Cache management
  getCache() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      
      // Check if cache is still valid
      if (Date.now() - timestamp > this.cacheExpiry) {
        localStorage.removeItem(this.cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Cache read error:', error);
      return null;
    }
  }

  setCache(data) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Cache write error:', error);
    }
  }

  // Fetch all product data in parallel
  async refreshProductData() {
    const handles = [...new Set(
      this.events
        .filter(e => e.needsProduct && e.product_handle && e.product_handle !== 'none')
        .map(e => e.product_handle)
    )];

    if (handles.length === 0) return;

    const productPromises = handles.map(handle => 
      fetch(`/products/${handle}.js`)
        .then(response => {
          if (response.ok) {
            return response.json();
          } else if (response.status === 404) {
            return { notFound: true };
          } else {
            throw new Error(`HTTP ${response.status}`);
          }
        })
        .then(product => {
          if (product && product.notFound) {
            this.productCache.set(handle, {
              image: null,
              available: false,
              isNotFound: true,
              variants: []
            });
          } else if (product) {
            this.productCache.set(handle, {
              image: product.featured_image || product.images?.[0] || null,
              available: product.available,
              isNotFound: false,
              variants: product.variants
            });
          }
          return { handle, product };
        })
        .catch(error => {
          console.warn(`Failed to fetch ${handle}:`, error);
          this.productCache.set(handle, {
            image: null,
            available: false,
            isNotFound: true,
            variants: []
          });
          return { handle, product: null };
        })
    );

    await Promise.all(productPromises);

    // Update events with product data
    let updated = false;
    this.events.forEach(event => {
      if (!event.needsProduct) return;
      
      const productData = this.productCache.get(event.product_handle);
      if (!productData) return;

      if (!event.product_image && productData.image) {
        event.product_image = productData.image;
        updated = true;
      }

      if (event.availability === 'open') {
        if (productData.isNotFound) {
          event.availability = 'coming_soon';
          event.availability_label = 'Coming Soon';
          updated = true;
        } else if (!productData.available) {
          event.availability = 'sold_out';
          event.availability_label = 'Sold Out';
          updated = true;
        } else if (productData.variants?.length > 0) {
          const anyAvailable = productData.variants.some(v => v.available);
          if (!anyAvailable) {
            event.availability = 'sold_out';
            event.availability_label = 'Sold Out';
            updated = true;
          }
        }
      }
    });

    if (updated) {
      this.applyFilters();
      this.setCache(this.events);
    }
  }

  cleanLocationName(location) {
    if (location.includes('http')) return '';
    
    if (location.includes(',')) {
      const parts = location.split(',');
      for (let part of parts) {
        part = part.trim().toLowerCase();
        if (part.includes('london')) return 'London';
        if (part.includes('los angeles')) return 'Los Angeles';
        if (part.includes('new york')) return 'New York';
      }
    }
    
    return location;
  }

  getLocationBadgeClass(location) {
    const locationLower = location.toLowerCase();
    
    if (locationLower.includes('los angeles')) return 'available';
    if (locationLower.includes('new york')) return 'soldout';
    if (locationLower.includes('london')) return 'available';
    
    return 'available';
  }

  extractProductInfo(description) {
    const handleMatch = description.match(/ProductHandle:\s*([a-z0-9-]+)/i);
    if (handleMatch) {
      const handle = handleMatch[1];
      return { 
        url: handle === 'none' ? '#' : `/products/${handle}`, 
        handle: handle 
      };
    }
    const urlMatch = description.match(/\/products\/([a-z0-9-]+)/i);
    if (urlMatch) {
      return { url: `/products/${urlMatch[1]}`, handle: urlMatch[1] };
    }
    return { url: '#', handle: '' };
  }

  extractAvailability(description) {
    const match = description.match(/Availability:\s*(Open|Sold Out|Coming Soon)/i);
    if (match) {
      const value = match[1].toLowerCase().replace(/\s+/g, '_');
      return value;
    }
    return null;
  }

  getAvailabilityLabel(availability) {
    switch(availability) {
      case 'sold_out':
        return 'Sold Out';
      case 'coming_soon':
        return 'Coming Soon';
      case 'free':
        return 'Free';
      case 'open':
      default:
        return 'Open';
    }
  }

  extractImage(description) {
    const match = description.match(/Image:\s*(https?:\/\/[^\s]+)/i);
    return match ? match[1].trim() : null;
  }

  isUpcoming(dateString) {
    return new Date(dateString) >= new Date();
  }

  formatDateDisplay(start, end, allDay) {
    let endDate = new Date(end);
    if (allDay) endDate.setDate(endDate.getDate() - 1);
    
    const format = (d) => {
      const day = String(d.getDate()).padStart(2, '0');
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}/${m}`; // DD/MM format
    };
    
    const startFmt = format(start);
    const endFmt = format(endDate);
    return startFmt === endFmt ? startFmt : `${startFmt} - ${endFmt}`;
  }

  populateLocationFilters() {
    if (!this.elements.locationFilters) return;
    
    // Only show locations from events within the next 30 days
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    const eventsWithin30Days = this.events.filter(event => {
      const eventStart = new Date(event.start);
      return eventStart <= thirtyDaysFromNow;
    });
    
    const locations = [...new Set(eventsWithin30Days.map(e => e.location).filter(Boolean))].sort();
    
    if (locations.length === 0) return;

    this.elements.locationFilters.innerHTML = locations.map(location => `
      <label class="events-calendar__filter-option">
        <input type="checkbox" value="${this.escapeHtml(location)}" data-filter-location-input>
        <span>${this.escapeHtml(location)}</span>
      </label>
    `).join('');

    this.elements.locationFilters.querySelectorAll('[data-filter-location-input]').forEach(input => {
      input.addEventListener('change', () => this.handleLocationFilter(input));
    });
  }

  handleLocationFilter(input) {
    if (input.checked) {
      this.filters.locations.add(input.value);
    } else {
      this.filters.locations.delete(input.value);
    }
    this.applyFilters();
  }

  handleAvailabilityFilter(input) {
    if (input.checked) {
      this.filters.availability.add(input.value);
    } else {
      this.filters.availability.delete(input.value);
    }
    this.applyFilters();
  }

  handleDateFilter(input) {
    this.filters.dateRange = input.value;
    this.applyFilters();
  }

  applyFilters() {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    this.filteredEvents = this.events.filter(event => {
      const eventStart = new Date(event.start);
      
      if (this.filters.locations.size > 0 && !this.filters.locations.has(event.location)) {
        return false;
      }

      if (this.filters.availability.size > 0) {
        const availabilityValue = event.availability === 'sold_out' ? 'sold_out' : 'open';
        if (!this.filters.availability.has(availabilityValue)) {
          return false;
        }
      }

      // Date range filter - with 30 days as maximum
      switch (this.filters.dateRange) {
        case 'week':
          if (eventStart > weekFromNow) return false;
          break;
        case 'month':
        case 'all':
        default:
          // Maximum is always 30 days
          if (eventStart > thirtyDaysFromNow) return false;
          break;
      }

      return true;
    });

    this.renderEvents();
    this.renderPreview();
    this.updateFilterCount();
  }

  updateFilterCount() {
    if (!this.elements.filterCount) return;
    
    const count = this.filters.locations.size + 
                  this.filters.availability.size + 
                  (this.filters.dateRange !== 'all' ? 1 : 0);

    if (count > 0) {
      this.elements.filterCount.textContent = `(${count})`;
      this.elements.filterCount.classList.remove('hidden');
    } else {
      this.elements.filterCount.classList.add('hidden');
    }
  }

  clearAllFilters() {
    this.filters.locations.clear();
    this.filters.availability.clear();
    this.filters.dateRange = 'all';

    this.container.querySelectorAll('[data-filter-location-input], [data-filter-availability-input]')
      .forEach(input => input.checked = false);
    
    const allDateInput = this.container.querySelector('[data-filter-date-input][value="all"]');
    if (allDateInput) {
      allDateInput.checked = true;
    }

    this.applyFilters();
  }

  renderEvents() {
    if (this.filteredEvents.length === 0) {
      this.elements.eventsList.innerHTML = '';
      this.elements.emptyState.hidden = false;
      return;
    }

    this.elements.emptyState.hidden = true;

    this.elements.eventsList.innerHTML = this.filteredEvents.map((event, index) => {
      const availabilityClass = event.availability === 'sold_out' ? 'soldout' : 
                                event.availability === 'coming_soon' ? 'coming-soon' : 
                                event.availability === 'free' ? 'free' :
                                'available';
      
      return `
      <a href="${this.escapeHtml(event.product_url)}" 
         class="events-calendar__item" 
         data-event-index="${index}"
         data-event-id="${event.id}"
         style="--item-bg-image: url('${event.product_image || ''}');">
        <div class="events-calendar__item-inner">
          <div class="events-calendar__item-date">
            ${this.escapeHtml(event.date_display)}
          </div>
          <div class="events-calendar__item-content">
            <h3 class="events-calendar__item-title">${this.escapeHtml(event.title)}</h3>
          </div>
          <div class="events-calendar__item-status">
            ${event.location ? `
              <span class="badge badge--${event.location_badge_class}">
                ${this.escapeHtml(event.location).toUpperCase()}
              </span>
            ` : ''}
            <span class="badge badge--availability badge--${availabilityClass}">
              ${this.escapeHtml(event.availability_label).toUpperCase()}
            </span>
          </div>
        </div>
      </a>
      `;
    }).join('');

    this.elements.eventsList.querySelectorAll('[data-event-index]').forEach(item => {
      item.addEventListener('mouseenter', () => {
        const index = parseInt(item.dataset.eventIndex);
        this.showPreview(index);
        item.classList.add('is-active');
      });
      
      item.addEventListener('mouseleave', () => {
        item.classList.remove('is-active');
      });
    });
  }

  renderPreview() {
    if (!this.elements.previewContainer) return;
    
    this.elements.previewContainer.innerHTML = this.filteredEvents.map((event, index) => `
      <div class="events-calendar__preview-image ${index === 0 ? 'is-active' : ''}" 
           data-preview-index="${index}"
           style="background-image: url('${event.product_image || ''}');">
      </div>
      
      <div class="events-calendar__preview-top ${index === 0 ? 'is-active' : ''}" 
           data-preview-top="${index}">
        <div class="events-calendar__preview-event-info">
          <div class="events-calendar__preview-info-group">
            <p class="events-calendar__preview-label">Event</p>
            <p class="events-calendar__preview-value">${this.escapeHtml(event.title)}</p>
          </div>
        </div>
        <div class="events-calendar__preview-availability">
          <span class="events-calendar__preview-badge badge--${event.availability === 'sold_out' ? 'soldout' : event.availability === 'coming_soon' ? 'coming-soon' : event.availability === 'free' ? 'free' : 'available'}">
            ${this.escapeHtml(event.availability_label).toUpperCase()}
          </span>
        </div>
      </div>
      
      <div class="events-calendar__preview-details ${index === 0 ? 'is-active' : ''}" 
           data-preview-details="${index}">
        <div class="events-calendar__preview-date">${this.escapeHtml(event.date_display)}</div>
        <h2 class="events-calendar__preview-title">${this.escapeHtml(event.title)}</h2>
      </div>
    `).join('');
  }

  showPreview(index) {
    if (!this.elements.previewContainer) return;
    
    this.elements.previewContainer.querySelectorAll('.is-active').forEach(el => {
      if (el && el.classList) {
        el.classList.remove('is-active');
      }
    });
    
    const image = this.elements.previewContainer.querySelector(`[data-preview-index="${index}"]`);
    const top = this.elements.previewContainer.querySelector(`[data-preview-top="${index}"]`);
    const details = this.elements.previewContainer.querySelector(`[data-preview-details="${index}"]`);
    
    if (image && image.classList) image.classList.add('is-active');
    if (top && top.classList) top.classList.add('is-active');
    if (details && details.classList) details.classList.add('is-active');
    
    this.currentPreviewIndex = index;
  }

  showLoading() {
    this.elements.loading.hidden = false;
    this.elements.eventsList.hidden = true;
    this.elements.emptyState.hidden = true;
    this.elements.errorState.hidden = true;
  }

  hideLoading() {
    this.elements.loading.hidden = true;
    this.elements.eventsList.hidden = false;
  }

  showError() {
    this.elements.errorState.hidden = false;
    this.elements.emptyState.hidden = true;
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, m => map[m]);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEventsCalendar);
} else {
  initEventsCalendar();
}

function initEventsCalendar() {
  const container = document.querySelector('[data-events-calendar]');
  if (container) {
    new EventsCalendar(container);
  }
}